import classNames from "classnames";
import React, { useEffect } from "react";
import * as d3 from "d3";
import { geoContains, geoGraticule10 } from "d3-geo";
import { geoInertiaDrag } from "./d3-inertia";
import world from "world-atlas/countries-50m.json";
import * as topojson from "topojson-client";
import * as topoSimplify from "topojson-simplify";
import { useStoreState, useStoreActions } from "../../store";
import * as Versor from "versor";
import countriesData from "../../lib/countries";

class GlobeOrthographic {
  context: CanvasRenderingContext2D;
  canvas: d3.Selection<HTMLCanvasElement, unknown, null, undefined>;
  width: number;
  height: number;
  projection: d3.GeoProjection;
  path: d3.GeoPath;
  features: any;

  state = {
    clickedCountry: null
  };

  constructor(container: HTMLDivElement) {
    this.width = container.offsetWidth;
    this.height = container.offsetHeight;
    const radius = this.height / 2 - 5;
    const padding = 30;
    const scale = radius;
    const pixelRatio = 1; // window.devicePixelRatio || 1;

    this.canvas = d3
      .select(container)
      .append("canvas")
      .attr("width", this.width * pixelRatio)
      .attr("height", this.height * pixelRatio)
      .style("width", this.width + "px")
      .style("height", this.height + "px");

    this.canvas.on("click", () => {
      const country = getCountry(this.canvas.node());
      // console.log("clicked", country);
      this.state.clickedCountry = country;

      // find alapha-2 ISO3166 code based on numeric
      const countryData = countriesData.find(
        c => c["country-code"] === country.id
      );
      if (country) this.focusCountry(countryData["alpha-2"]);
    });

    const getCountry = element => {
      const pos = this.projection.invert(d3.mouse(element));
      return this.features.countries.find(f => geoContains(f, pos));
    };

    this.context = this.canvas.node().getContext("2d");
    this.context.scale(pixelRatio, pixelRatio);

    this.projection = d3.geoOrthographic().fitExtent(
      [
        [padding, padding],
        [this.width - padding, this.height - padding]
      ],
      { type: "Sphere" }
    );

    this.path = d3.geoPath(this.projection, this.context);

    // let word_simplified = topoSimplify.presimplify(world);
    // let min_weight = topoSimplify.quantile(word_simplified, 0.049);
    // Every arc coordinate whose z-value is lower than min_weight is removed
    // let world = topoSimplify.simplify(word_simplified, min_weight);

    // const world = topoSimplify.simplify(worldPath, 1);

    const graticule = geoGraticule10();
    const land = topojson.feature(world, world.objects.countries);
    const countries = topojson.feature(world, world.objects.countries).features;
    const borders = topojson.mesh(
      world,
      world.objects.countries
      // (a, b) => a !== b // show only borders between countries
    );

    this.features = {
      graticule,
      land,
      countries,
      borders
    };

    // d3.select(canvas.node()).call(
    //   d3
    //     .zoom()
    //     .scaleExtent([1, 8])
    //     .on("zoom", zoom)
    // );

    // function zoom() {
    //   const transform = d3.event.transform;
    //   context.save();
    //   context.clearRect(0, 0, width, height);
    //   context.translate(transform.x, transform.y);
    //   context.scale(transform.k, transform.k);
    //   render();
    //   context.restore();
    // }
    this.render();
    this.initControls();
  }

  public initControls() {
    geoInertiaDrag(this.canvas, () => this.render(), this.projection);
  }

  public focusCountry(country: string) {
    console.log(country);
    const countryData = countriesData.find(
      c => c["alpha-2"] === country.toUpperCase()
    ); // find numeric ISO3166 based on alapha-2 code
    const countryFeature = this.features.countries.find(
      c => c.id === countryData["country-code"]
    );

    this.state.clickedCountry = countryFeature;
    const rotation = this.projection.rotate();
    const targetCoords = d3.geoCentroid(countryFeature);
    const targetRotation = [-targetCoords[0], -targetCoords[1], 0];

    const interpolateAngle = Versor.interpolate(rotation, targetRotation);

    d3.transition()
      .duration(500)
      .tween("render", () => t => {
        this.projection.rotate(interpolateAngle(t));
        this.render();
      })
      .end();
  }

  public render() {
    const { context, width, height, path } = this;
    const { graticule, countries, borders, land } = this.features;
    const { clickedCountry } = this.state;

    context.clearRect(0, 0, width, height);

    // render oceans and border
    context.beginPath();
    path({ type: "Sphere" });
    context.fillStyle = "#59bdf9";
    context.fill();
    context.strokeStyle = "#000";
    context.lineWidth = 1.5;
    context.stroke();

    // render graticule
    context.beginPath();
    path(graticule);
    context.lineWidth = 0.2;
    context.strokeStyle = "#000";
    context.stroke();

    // render land
    context.beginPath();
    path(land);
    context.fillStyle = "#ccc";
    context.fill();

    // render countries
    // context.beginPath();
    // path(countries);
    // context.fillStyle = "#ccc";
    // context.fill();

    if (clickedCountry?.id) {
      const selectedCountry = countries.find(c => c.id === clickedCountry.id);
      context.beginPath();
      path(selectedCountry);
      context.fillStyle = "#64cb56";
      context.fill();
    }

    // render borders
    context.beginPath();
    path(borders);
    context.strokeStyle = "#000";
    context.lineWidth = 0.5;
    context.stroke();
  }
}

export function GlobeD3() {
  const worldRef = React.useRef<HTMLDivElement>();
  const globeRef = React.useRef<any>({ current: null });

  const valentineMode = useStoreState(s => s.valentineMode);
  const focusedCountry = useStoreState(s => s.focusedCountry);
  const sidebarOpen = useStoreState(s => s.sidebarOpen);
  const setDetailsCountry = useStoreActions(s => s.setDetailsCountry);

  useEffect(() => {
    globeRef.current = new GlobeOrthographic(worldRef.current);
  }, []);

  useEffect(() => {
    globeRef.current.focusCountry(focusedCountry);
  }, [focusedCountry]);

  return (
    <div
      className={classNames(
        "map-container",
        valentineMode && "map-container--valentine"
      )}
      ref={worldRef}
    />
  );
}
